import type { CategoryBandDto, PriceMoveDto } from '../api/marketAnalytics'

export type WeeklyBand = {
  week_start: string
  week_end: string
  product_type_id: number
  source_kind: string
  unit: string
  min_price: number
  max_price: number
  avg_price: number
  median_price: number
  sample_count: number
}

export const formatAnalyticsRub = (amount: number) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 2 }).format(
    amount,
  )

export const formatAnalyticsPct = (value: number | null) => {
  if (value == null || Number.isNaN(value)) {
    return '—'
  }
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

export const formatAnalyticsDateTime = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Компактная дата (без времени) для подписей оси — из полного ISO-таймстампа. */
export const formatAnalyticsDateShort = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

export const formatAnalyticsDay = (isoDate: string) => {
  const date = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) {
    return isoDate
  }
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

export const formatAnalyticsWeekLabel = (weekStart: string, weekEnd: string) =>
  `${formatAnalyticsDay(weekStart)} – ${formatAnalyticsDay(weekEnd)}`

/** Понедельник ISO-недели (UTC) для даты YYYY-MM-DD. */
export const isoWeekStartUtc = (isoDate: string): string => {
  const date = new Date(`${isoDate}T00:00:00Z`)
  const day = date.getUTCDay() // 0=Sun … 6=Sat
  const offset = day === 0 ? -6 : 1 - day
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}

export const isoWeekEndUtc = (weekStart: string): string => {
  const date = new Date(`${weekStart}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + 6)
  return date.toISOString().slice(0, 10)
}

/** Схлопывает дневные коридоры в недельные (min/max по неделе, avg взвешен по sample_count). */
export const aggregateBandsByWeek = (bands: CategoryBandDto[]): WeeklyBand[] => {
  type Acc = {
    week_start: string
    product_type_id: number
    source_kind: string
    unit: string
    min_price: number
    max_price: number
    weightedAvg: number
    weightedMedian: number
    sample_count: number
  }
  const byKey = new Map<string, Acc>()

  for (const band of bands) {
    const weekStart = isoWeekStartUtc(band.band_date)
    const key = `${weekStart}|${band.unit}|${band.source_kind}|${band.product_type_id}`
    const weight = Math.max(band.sample_count, 1)
    const prev = byKey.get(key)
    if (!prev) {
      byKey.set(key, {
        week_start: weekStart,
        product_type_id: band.product_type_id,
        source_kind: band.source_kind,
        unit: band.unit,
        min_price: band.min_price,
        max_price: band.max_price,
        weightedAvg: band.avg_price * weight,
        weightedMedian: band.median_price * weight,
        sample_count: weight,
      })
      continue
    }
    prev.min_price = Math.min(prev.min_price, band.min_price)
    prev.max_price = Math.max(prev.max_price, band.max_price)
    prev.weightedAvg += band.avg_price * weight
    prev.weightedMedian += band.median_price * weight
    prev.sample_count += weight
  }

  return [...byKey.values()]
    .map((acc) => ({
      week_start: acc.week_start,
      week_end: isoWeekEndUtc(acc.week_start),
      product_type_id: acc.product_type_id,
      source_kind: acc.source_kind,
      unit: acc.unit,
      min_price: acc.min_price,
      max_price: acc.max_price,
      avg_price: acc.weightedAvg / acc.sample_count,
      median_price: acc.weightedMedian / acc.sample_count,
      sample_count: acc.sample_count,
    }))
    .sort((left, right) => left.week_start.localeCompare(right.week_start))
}

/** Для покупателя снижение листовой цены — позитивный сигнал. */
export const priceMoveToneClass = (deltaAbs: number) => {
  if (deltaAbs < 0) {
    return 'market-analytics-delta market-analytics-delta--down'
  }
  if (deltaAbs > 0) {
    return 'market-analytics-delta market-analytics-delta--up'
  }
  return 'market-analytics-delta'
}

export const sortPriceMovesByDeltaPct = (moves: PriceMoveDto[], direction: 'asc' | 'desc' = 'desc') =>
  [...moves].sort((left, right) => {
    const leftPct = left.delta_pct ?? 0
    const rightPct = right.delta_pct ?? 0
    return direction === 'desc' ? rightPct - leftPct : leftPct - rightPct
  })

const normalizeAnalyticsSearch = (value: string) =>
  value.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е')

export type PriceMoveSortKey = 'item' | 'supplier' | 'previous' | 'current' | 'delta' | 'when'
export type PriceMoveSortDir = 'asc' | 'desc'

export const priceMoveItemLabel = (
  move: PriceMoveDto,
  typeName: (id: number | null) => string,
) => move.item_name?.trim() || typeName(move.product_type_id)

export const filterPriceMovesByName = (
  moves: PriceMoveDto[],
  query: string,
  typeName: (id: number | null) => string,
) => {
  const needle = normalizeAnalyticsSearch(query)
  if (!needle) {
    return moves
  }
  return moves.filter((move) => {
    const name = normalizeAnalyticsSearch(priceMoveItemLabel(move, typeName))
    const sku = String(move.catalog_item_id)
    return name.includes(needle) || sku.includes(needle)
  })
}

export const sortPriceMovesByColumn = (
  moves: PriceMoveDto[],
  key: PriceMoveSortKey,
  dir: PriceMoveSortDir,
  typeName: (id: number | null) => string,
) => {
  const sign = dir === 'asc' ? 1 : -1
  return [...moves].sort((left, right) => {
    switch (key) {
      case 'item':
        return (
          priceMoveItemLabel(left, typeName).localeCompare(priceMoveItemLabel(right, typeName), 'ru') * sign
        )
      case 'supplier':
        return (left.supplier_name ?? '').localeCompare(right.supplier_name ?? '', 'ru') * sign
      case 'previous':
        return (left.previous_price - right.previous_price) * sign
      case 'current':
        return (left.current_price - right.current_price) * sign
      case 'delta':
        return ((left.delta_pct ?? 0) - (right.delta_pct ?? 0)) * sign
      default:
        return left.observed_at.localeCompare(right.observed_at) * sign
    }
  })
}

export type SupplierPriceMoveSummary = {
  moveCount: number
  upCount: number
  downCount: number
  avgDeltaPct: number | null
  topUp: PriceMoveDto[]
  topDown: PriceMoveDto[]
}

/** Сводка по последним изменениям в прайсе поставщика: среднее Δ% и ТОП роста/снижения. */
export const buildSupplierPriceMoveSummary = (
  moves: PriceMoveDto[],
  topN = 5,
): SupplierPriceMoveSummary => {
  const withPct = moves.filter((move) => move.delta_pct != null && !Number.isNaN(move.delta_pct))
  const avgDeltaPct =
    withPct.length === 0
      ? null
      : withPct.reduce((sum, move) => sum + (move.delta_pct as number), 0) / withPct.length
  const upMoves = moves.filter((move) => move.delta_abs > 0)
  const downMoves = moves.filter((move) => move.delta_abs < 0)
  const topUp = sortPriceMovesByDeltaPct(upMoves, 'desc').slice(0, topN)
  const topDown = sortPriceMovesByDeltaPct(downMoves, 'asc').slice(0, topN)
  return {
    moveCount: moves.length,
    upCount: upMoves.length,
    downCount: downMoves.length,
    avgDeltaPct,
    topUp,
    topDown,
  }
}
